import { Badge, Drawer, Group, Loader, Stack, Table, Text, Title } from "@mantine/core";

import { usePile } from "../../api/generated/stockvol";
import type { LabAttributeOut, PileBlocksOut, PileHistory } from "../../api/generated/model";
import { RequireRole } from "../../auth";
import { coverageText, fmtLab, type LabKey } from "../../lib/lab";
import { formatLocal } from "../../lib/time";
import { PileEditor } from "./PileEditor";
import { fmt0 } from "./tables";

/** The lab values of the coal on the pile in the latest sweep, and of what was reclaimed. */
function CoalQuality({ coal, attrs }: { coal: PileBlocksOut; attrs: LabAttributeOut[] }) {
  const taken = coal.removed.volume_m3 > 0;
  return (
    <>
      <Title order={6}>Coal quality</Title>
      <Table fz="xs" data-testid="pile-coal-quality">
        <Table.Thead>
          <Table.Tr>
            <Table.Th />
            <Table.Th ta="right">On the pile</Table.Th>
            {taken && <Table.Th ta="right">Reclaimed since the previous sweep</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <Table.Tr>
            <Table.Td>Tonnes</Table.Td>
            <Table.Td ta="right">{fmt0(coal.standing.tonnes_t)}</Table.Td>
            {taken && <Table.Td ta="right">{fmt0(coal.removed.tonnes_t)}</Table.Td>}
          </Table.Tr>
          {attrs.map((a) => (
            <Table.Tr key={a.key}>
              <Table.Td>{a.unit ? `${a.label} (${a.unit})` : a.label}</Table.Td>
              <Table.Td ta="right">{fmtLab(coal.standing.values[a.key as LabKey], a)}</Table.Td>
              {taken && <Table.Td ta="right">{fmtLab(coal.removed.values[a.key as LabKey], a)}</Table.Td>}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Text size="xs" c="dimmed">
        Tonnes-weighted, {coverageText(coal.standing.coverage)}. The 3D view's Blocks mode shows the
        pile's layers.
      </Text>
    </>
  );
}

/** One pile's history and lineage (merges and splits), newest first; its coal quality when the
 * block model has it (M7.9). */
export function LineageDrawer({
  code,
  tz,
  onClose,
  coal,
  attrs = [],
}: {
  code: string | null;
  tz: string;
  onClose: () => void;
  coal?: PileBlocksOut | undefined;
  attrs?: LabAttributeOut[];
}) {
  const pile = usePile(code ?? "", undefined, {
    query: { enabled: code !== null, select: (r) => (r as { data: PileHistory }).data },
  });
  const p = pile.data;
  return (
    <Drawer opened={code !== null} onClose={onClose} position="right" size="md" title={code ?? ""}>
      {pile.isPending && <Loader size="sm" />}
      {p && (
        <Stack gap="sm">
          <Group gap={6}>
            <Text size="sm">
              {p.label ?? "no name"} · {p.product ?? "no product"}
            </Text>
            <Badge size="xs">{p.status}</Badge>
          </Group>
          <RequireRole min="operator">
            <PileEditor key={p.code} pile={p} />
          </RequireRole>
          {coal && <CoalQuality coal={coal} attrs={attrs} />}
          <Title order={6}>Lineage</Title>
          {p.lineage.length === 0 ? (
            <Text size="sm" c="dimmed">
              No merges or splits.
            </Text>
          ) : (
            <Table fz="xs">
              <Table.Tbody>
                {p.lineage.map((l, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>{String(l["relation"])}</Table.Td>
                    <Table.Td>{String(l["other"] ?? "—")}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
          <Title order={6}>History</Title>
          <Table fz="xs" data-testid="pile-history">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Sweep</Table.Th>
                <Table.Th ta="right">m³</Table.Th>
                <Table.Th ta="right">t</Table.Th>
                <Table.Th>State</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {[...p.history].reverse().map((h) => (
                <Table.Tr key={h.run_id}>
                  <Table.Td>{formatLocal(h.t, tz)}</Table.Td>
                  <Table.Td ta="right">{fmt0(h.v_best_m3)}</Table.Td>
                  <Table.Td ta="right">{fmt0(h.tonnes_t)}</Table.Td>
                  <Table.Td>{h.lifecycle ?? ""}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      )}
    </Drawer>
  );
}
